export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiRequestError extends Error {
  constructor(readonly status: number, readonly error: ApiError) {
    super(error.message);
  }
}

let accessToken: string | null = null;
let refreshToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setTokens(next: { accessToken: string; refreshToken: string } | null): void {
  accessToken = next?.accessToken ?? null;
  refreshToken = next?.refreshToken ?? null;
}

export function onSessionExpired(handler: () => void): void {
  onSessionLost = handler;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

async function raw<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiRequestError(res.status, (body as { error?: ApiError }).error ?? {
      code: 'unknown', message: 'The server did not respond as expected.',
    });
  }
  return body as T;
}

/** One transparent refresh attempt, then the session is treated as gone. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return await raw<T>(path, init);
  } catch (err) {
    if (!(err instanceof ApiRequestError) || err.status !== 401 || !refreshToken) throw err;

    try {
      const renewed = await raw<{ accessToken: string; refreshToken: string }>(
        '/api/v1/auth/refresh',
        { method: 'POST', body: JSON.stringify({ refreshToken }) },
      );
      setTokens(renewed);
      return await raw<T>(path, init);
    } catch (refreshErr) {
      setTokens(null);
      onSessionLost?.();
      throw refreshErr;
    }
  }
}

export interface Session {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: { id: string; email: string; fullName: string; employeeId: string | null; roles: string[]; permissions: string[] };
  tenant: { id: string; slug: string; name: string };
}

export function login(tenantSlug: string, email: string, password: string): Promise<Session> {
  return raw<Session>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ tenantSlug, email, password }),
  });
}

export function logout(): Promise<{ ok: boolean }> {
  return api('/api/v1/auth/logout', { method: 'POST', body: '{}' });
}

// ----------------- EMPLOYEES & ORG -----------------
export interface EmployeeRecord {
  id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  designation: string | null;
  department_id: string | null;
  status: string;
  work_email: string | null;
  personal_email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  date_of_joining: string;
  location: string | null;
  employment_type: string;
  compensation?: {
    ctc_annual: number;
    basic_monthly: number;
    hra_monthly: number;
    other_allowance_monthly: number;
    effective_from: string;
    currency: string;
  } | null;
}

export interface Paged<T> { items: T[]; total: number; page: number; pageSize: number }

export function listEmployees(params: { search?: string; status?: string; page?: number } = {}) {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.status) q.set('status', params.status);
  q.set('page', String(params.page ?? 1));
  return api<Paged<EmployeeRecord>>(`/api/v1/employees?${q.toString()}`);
}

export function getEmployee(id: string) {
  return api<EmployeeRecord>(`/api/v1/employees/${id}`);
}

export interface Department { id: string; code: string; name: string; cost_center: string | null }
export function listDepartments() {
  return api<{ items: Department[] }>('/api/v1/departments');
}

export interface AuditEntry {
  id: string;
  action: string;
  module: string;
  resource_type: string;
  resource_id: string | null;
  reason: string | null;
  created_at: string;
  actor_user_id: string | null;
}
export function listAuditLogs(resourceId?: string) {
  const q = resourceId ? `?resourceId=${resourceId}` : '';
  return api<{ items: AuditEntry[] }>(`/api/v1/audit-logs${q}`);
}

// ----------------- DASHBOARD -----------------
export interface DashboardSummary {
  workforce: {
    totalEmployees: number;
    activeEmployees: number;
    totalDepartments: number;
    presentToday: number;
    openPositions: number;
  };
  approvals: {
    pendingLeaves: number;
    pendingExpenses: number;
    pendingResignations: number;
  };
  payroll: {
    id: string;
    month: string;
    total_employees: number;
    total_gross: number;
    total_net_pay: number;
    total_tax_tds: number;
    status: string;
  } | null;
  personal: {
    attendanceToday: AttendanceLog | null;
    leaveBalances: Array<{
      allocated_days: number;
      used_days: number;
      pending_days: number;
      name: string;
      code: string;
    }>;
  };
}

export function getDashboardSummary() {
  return api<{ data: DashboardSummary }>('/api/v1/dashboard/summary');
}

// ----------------- ATTENDANCE -----------------
export interface Shift {
  id: string;
  name: string;
  code: string;
  start_time: string;
  end_time: string;
  break_minutes: number;
  is_night_shift: number;
}

export interface AttendanceLog {
  id: string;
  employee_id: string;
  date: string;
  clock_in: string | null;
  clock_out: string | null;
  total_work_minutes: number;
  status: string;
  ip_address: string | null;
  notes: string | null;
  first_name?: string;
  last_name?: string;
  employee_code?: string;
}

export function listShifts() {
  return api<{ data: Shift[] }>('/api/v1/attendance/shifts');
}

export function listAttendanceLogs(params: { employeeId?: string; month?: string } = {}) {
  const q = new URLSearchParams();
  if (params.employeeId) q.set('employeeId', params.employeeId);
  if (params.month) q.set('month', params.month);
  return api<{ data: AttendanceLog[] }>(`/api/v1/attendance/logs?${q.toString()}`);
}

export function clockIn(body: { latitude?: number; longitude?: number; notes?: string } = {}) {
  return api<{ data: AttendanceLog }>('/api/v1/attendance/clock-in', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function clockOut() {
  return api<{ data: AttendanceLog }>('/api/v1/attendance/clock-out', {
    method: 'POST',
    body: '{}',
  });
}

export function regularizeAttendance(body: { date: string; requestedClockIn?: string; requestedClockOut?: string; reason: string }) {
  return api<{ data: any }>('/api/v1/attendance/regularize', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ----------------- LEAVE -----------------
export interface LeaveType {
  id: string;
  code: string;
  name: string;
  days_per_year: number;
  is_paid: number;
}

export interface LeaveBalance {
  id: string;
  employee_id: string;
  leave_type_id: string;
  year: number;
  allocated_days: number;
  used_days: number;
  pending_days: number;
  leave_type_name: string;
  leave_type_code: string;
  is_paid: number;
}

export interface LeaveRequest {
  id: string;
  employee_id: string;
  leave_type_id: string;
  start_date: string;
  end_date: string;
  total_days: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_remarks: string | null;
  created_at: string;
  leave_type_name: string;
  leave_type_code: string;
  first_name: string;
  last_name: string;
  employee_code: string;
}

export function listLeaveTypes() {
  return api<{ data: LeaveType[] }>('/api/v1/leave/types');
}

export function listLeaveBalances(employeeId?: string, year?: number) {
  const q = new URLSearchParams();
  if (employeeId) q.set('employeeId', employeeId);
  if (year) q.set('year', String(year));
  return api<{ data: LeaveBalance[] }>(`/api/v1/leave/balances?${q.toString()}`);
}

export function listLeaveRequests(params: { employeeId?: string; viewAll?: boolean } = {}) {
  const q = new URLSearchParams();
  if (params.employeeId) q.set('employeeId', params.employeeId);
  if (params.viewAll) q.set('viewAll', 'true');
  return api<{ data: LeaveRequest[] }>(`/api/v1/leave/requests?${q.toString()}`);
}

export function applyLeave(body: { leaveTypeId: string; startDate: string; endDate: string; totalDays: number; reason: string }) {
  return api<{ data: LeaveRequest }>('/api/v1/leave/requests', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function reviewLeave(id: string, body: { status: 'approved' | 'rejected'; remarks?: string | null }) {
  return api<{ data: LeaveRequest }>(`/api/v1/leave/requests/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ----------------- PAYROLL -----------------
export interface PayrollRun {
  id: string;
  organization_id: string;
  month: string;
  total_employees: number;
  total_gross: number;
  total_deductions: number;
  total_net_pay: number;
  total_tax_tds: number;
  total_pf_employer: number;
  status: 'draft' | 'finalized';
  processed_by: string;
  approved_by: string | null;
  finalized_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PayslipSummary {
  id: string;
  payroll_run_id?: string;
  employee_id: string;
  month: string;
  financial_year: string | null;
  working_days: number;
  paid_days: number;
  loss_of_pay_days: number;
  gross_earnings: number;
  total_deductions: number;
  statutory_deductions: number;
  income_tax_tds: number;
  net_salary: number;
  payment_status: string;
  status: 'draft' | 'reviewed' | 'approved' | 'published';
  published_at: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  downloaded_at: string | null;
  first_name: string;
  last_name: string;
  employee_code: string;
  designation: string | null;
  department_name?: string;
}

export interface LineItem {
  id: string;
  payslip_id: string;
  component_name: string;
  component_code: string;
  type: 'earning' | 'deduction' | 'employer_contribution';
  amount: number;
  is_statutory: number;
}

export interface MaskedBankInfo {
  bankName: string | null;
  accountNumber: string;
  ifsc: string | null;
}

export interface PayslipDetail {
  payslip: {
    id: string;
    payroll_run_id: string;
    employee_id: string;
    month: string;
    financial_year: string | null;
    working_days: number;
    paid_days: number;
    loss_of_pay_days: number;
    gross_earnings: number;
    total_deductions: number;
    statutory_deductions: number;
    income_tax_tds: number;
    net_salary: number;
    payment_status: string;
    payment_reference: string | null;
    status: 'draft' | 'reviewed' | 'approved' | 'published';
    published_at: string | null;
    sent_at: string | null;
    viewed_at: string | null;
    downloaded_at: string | null;
    created_at: string;
    first_name: string;
    last_name: string;
    employee_code: string;
    designation: string | null;
    organization_name: string;
    organization_pan: string | null;
    organization_gstin: string | null;
  };
  lineItems: LineItem[];
  maskedBank?: MaskedBankInfo | null;
}

export function listPayrollRuns() {
  return api<{ data: PayrollRun[] }>('/api/v1/payroll/runs');
}

export function getPayrollRun(id: string) {
  return api<{ data: { run: PayrollRun; payslips: PayslipSummary[] } }>(`/api/v1/payroll/runs/${id}`);
}

export function executePayrollRun(body: { month: string; organizationId: string }) {
  return api<{ data: { run: PayrollRun; payslips: PayslipSummary[] } }>('/api/v1/payroll/runs', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function finalizePayrollRun(id: string) {
  return api<{ data: { run: PayrollRun; payslips: PayslipSummary[] } }>(`/api/v1/payroll/runs/${id}/finalize`, {
    method: 'POST',
    body: '{}',
  });
}

export function publishPayrollRun(id: string) {
  return api<{ data: { run: PayrollRun; payslips: PayslipSummary[] } }>(`/api/v1/payroll/runs/${id}/publish`, {
    method: 'POST',
    body: '{}',
  });
}

export function listAllPayslips(query?: { month?: string; financialYear?: string; departmentId?: string; status?: string; search?: string }) {
  const params = new URLSearchParams();
  if (query?.month) params.set('month', query.month);
  if (query?.financialYear) params.set('financialYear', query.financialYear);
  if (query?.departmentId) params.set('departmentId', query.departmentId);
  if (query?.status) params.set('status', query.status);
  if (query?.search) params.set('search', query.search);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return api<{ data: PayslipSummary[] }>(`/api/v1/payroll/payslips${qs}`);
}

export function getPayslip(id: string) {
  return api<{ data: PayslipDetail }>(`/api/v1/payroll/payslips/${id}`);
}

export function getMyPayslips(query?: { financialYear?: string; month?: string }) {
  const params = new URLSearchParams();
  if (query?.financialYear) params.set('financialYear', query.financialYear);
  if (query?.month) params.set('month', query.month);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return api<{ data: PayslipSummary[] }>(`/api/v1/payroll/payslips/me${qs}`);
}

export function sharePayslip(id: string) {
  return api<{ data: { success: boolean; sentAt: string } }>(`/api/v1/payroll/payslips/${id}/share`, {
    method: 'POST',
    body: '{}',
  });
}

export function recordPayslipDownload(id: string) {
  return api<{ data: { success: boolean; downloadedAt: string } }>(`/api/v1/payroll/payslips/${id}/download-log`, {
    method: 'POST',
    body: '{}',
  });
}

export function submitTaxDeclaration(body: {
  financialYear: string;
  regime: 'new' | 'old';
  section80cAmount: number;
  section80dAmount: number;
  hraRentPaidAnnual: number;
  otherExemptions: number;
}) {
  return api<{ data: any }>('/api/v1/payroll/tax-declarations', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}


// ----------------- EXPENSES -----------------
export interface ExpenseClaim {
  id: string;
  employee_id: string;
  claim_number: string;
  title: string;
  total_amount: number;
  currency: string;
  status: 'submitted' | 'approved' | 'rejected';
  submitted_at: string;
  approved_by: string | null;
  approved_at: string | null;
  settled_at: string | null;
  created_at: string;
  first_name: string;
  last_name: string;
  employee_code: string;
}

export function listExpenseClaims(params: { employeeId?: string; viewAll?: boolean } = {}) {
  const q = new URLSearchParams();
  if (params.employeeId) q.set('employeeId', params.employeeId);
  if (params.viewAll) q.set('viewAll', 'true');
  return api<{ data: ExpenseClaim[] }>(`/api/v1/expenses?${q.toString()}`);
}

export function createExpenseClaim(body: {
  title: string;
  currency?: string;
  items: Array<{ category: string; spentDate: string; amount: number; merchant?: string | null; description?: string | null }>;
}) {
  return api<{ data: { claim: ExpenseClaim; items: any[] } }>('/api/v1/expenses', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function reviewExpenseClaim(id: string, status: 'approved' | 'rejected') {
  return api<{ data: { claim: ExpenseClaim; items: any[] } }>(`/api/v1/expenses/${id}/review`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

// ----------------- LOANS -----------------
export interface EmployeeLoan {
  id: string;
  employee_id: string;
  loan_type: 'advance' | 'personal_loan';
  principal_amount: number;
  interest_rate: number;
  total_repayable: number;
  emi_monthly_amount: number;
  tenure_months: number;
  remaining_balance: number;
  status: 'pending' | 'active' | 'closed' | 'rejected';
  disbursed_at: string | null;
  created_at: string;
  first_name: string;
  last_name: string;
  employee_code: string;
}

export function listLoans(params: { employeeId?: string; viewAll?: boolean } = {}) {
  const q = new URLSearchParams();
  if (params.employeeId) q.set('employeeId', params.employeeId);
  if (params.viewAll) q.set('viewAll', 'true');
  return api<{ data: EmployeeLoan[] }>(`/api/v1/loans?${q.toString()}`);
}

export function applyLoan(body: { loanType: 'advance' | 'personal_loan'; principalAmount: number; tenureMonths: number }) {
  return api<{ data: EmployeeLoan }>('/api/v1/loans', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function reviewLoan(id: string, status: 'active' | 'rejected') {
  return api<{ data: EmployeeLoan }>(`/api/v1/loans/${id}/review`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

// ----------------- ASSETS & LIFECYCLE -----------------
export interface AssetCategory {
  id: string;
  category_code: string;
  category_name: string;
  group_type: 'it_assets' | 'communication' | 'office' | 'travel_ops' | 'software_digital' | 'other';
  prefix: string;
  useful_life_months: number;
  default_depreciation_rate: number;
  description: string | null;
  is_active: number;
}

export interface AssetAccessory {
  id: string;
  asset_id: string;
  name: string;
  accessory_type: 'charger' | 'mouse' | 'bag' | 'cable' | 'dongle' | 'case' | 'other';
  serial_number: string | null;
  condition: string;
  status: 'included' | 'missing' | 'damaged';
  created_at: string;
}

export interface Asset {
  id: string;
  asset_code: string;
  asset_name?: string | null;
  category: string;
  category_id?: string | null;
  category_display_name?: string | null;
  category_group_type?: string | null;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  imei?: string | null;
  mac_address?: string | null;
  purchase_date: string | null;
  purchase_cost: number | null;
  condition: string;
  status:
    | 'in_stock'
    | 'available'
    | 'reserved'
    | 'allocated'
    | 'assigned'
    | 'in_use'
    | 'transfer_pending'
    | 'return_pending'
    | 'returned'
    | 'inspection_pending'
    | 'under_repair'
    | 'under_maintenance'
    | 'damaged'
    | 'lost'
    | 'reassigned'
    | 'disposed'
    | 'retired';
  warranty_start?: string | null;
  warranty_end?: string | null;
  warranty_provider?: string | null;
  warranty_notes?: string | null;
  location: string | null;
  department_id: string | null;
  department_name?: string | null;
  cost_center?: string | null;
  project_id?: string | null;
  current_holder_id: string | null;
  holder_first_name?: string | null;
  holder_last_name?: string | null;
  holder_employee_code?: string | null;
  vendor_id?: string | null;
  vendor_name?: string | null;
  useful_life_months?: number | null;
  current_book_value?: number | null;
  qr_code_payload?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmployeeAsset {
  allocation_id: string;
  asset_id: string;
  employee_id: string;
  allocated_at: string;
  expected_return_date?: string | null;
  returned_at: string | null;
  allocation_status: string;
  issued_condition?: string | null;
  accessories_included?: string | null;
  acknowledgement_status?: 'pending' | 'acknowledged' | 'disputed' | null;
  acknowledged_at?: string | null;
  asset_code: string;
  asset_name?: string | null;
  category: string;
  category_display_name?: string | null;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  imei?: string | null;
  condition: string;
  warranty_end?: string | null;
  asset_status?: string;
  location?: string | null;
  accessories?: AssetAccessory[];
}

export interface AssetDetail {
  asset: Asset;
  accessories: AssetAccessory[];
  allocationHistory: Array<{
    id: string;
    asset_id: string;
    employee_id: string;
    allocated_at: string;
    expected_return_date?: string | null;
    returned_at: string | null;
    status: string;
    issued_condition?: string | null;
    return_condition?: string | null;
    acknowledgement_status?: string | null;
    acknowledged_at?: string | null;
    employee_first_name?: string | null;
    employee_last_name?: string | null;
    employee_code?: string | null;
    work_email?: string | null;
  }>;
  transfers: Array<{
    id: string;
    asset_id: string;
    transfer_type: string;
    from_first_name?: string | null;
    from_last_name?: string | null;
    to_first_name?: string | null;
    to_last_name?: string | null;
    from_department_name?: string | null;
    to_department_name?: string | null;
    from_location?: string | null;
    to_location?: string | null;
    condition_at_transfer: string;
    reason: string;
    transfer_date: string;
  }>;
  maintenanceRecords: Array<{
    id: string;
    asset_id: string;
    maintenance_code: string;
    vendor_name?: string | null;
    service_type: string;
    issue_description: string;
    start_date: string;
    completion_date?: string | null;
    downtime_days: number;
    repair_cost: number;
    is_warranty_covered: number;
    status: string;
    resolution_notes?: string | null;
    next_scheduled_maintenance?: string | null;
  }>;
  returns: Array<{
    id: string;
    return_date: string;
    first_name?: string | null;
    last_name?: string | null;
    employee_code?: string | null;
    condition_on_return: string;
    inspection_result: string;
    has_damage: number;
    damage_description?: string | null;
    inspector_remarks?: string | null;
  }>;
  incidents: Array<{
    id: string;
    incident_type: string;
    incident_date: string;
    description: string;
    estimated_repair_or_replacement_cost: number;
    proposed_recovery_amount: number;
    status: string;
    first_name?: string | null;
    last_name?: string | null;
    employee_code?: string | null;
  }>;
  acknowledgements: Array<{
    id: string;
    terms_version: string;
    terms_text: string;
    ip_address?: string | null;
    user_agent?: string | null;
    status: string;
    comments?: string | null;
    acknowledged_at: string;
    first_name?: string | null;
    last_name?: string | null;
  }>;
}

export interface AssetDashboardSummary {
  kpis: {
    totalAssets: number;
    totalAcquisitionValue: number;
    currentAssetValue: number;
    availableAssets: number;
    assignedAssets: number;
    underMaintenance: number;
    damagedAssets: number;
    lostAssets: number;
    returnPending: number;
    returnedAssets: number;
    disposedAssets: number;
    assetsUnderWarranty: number;
    warrantyNearingExpiry: number;
    maintenanceDueNext30Days: number;
    pendingReturnsCount: number;
    activeIncidentsCount: number;
  };
  categoryBreakdown: Array<{ count: number; totalValue: number; categoryName: string }>;
  statusBreakdown: Record<string, number>;
  locationBreakdown: Record<string, number>;
  categories: AssetCategory[];
}

export function getAssetDashboardSummary(query: { departmentId?: string; location?: string } = {}) {
  const q = new URLSearchParams();
  if (query.departmentId) q.set('departmentId', query.departmentId);
  if (query.location) q.set('location', query.location);
  const qs = q.toString() ? `?${q.toString()}` : '';
  return api<{ data: AssetDashboardSummary }>(`/api/v1/assets/dashboard/summary${qs}`);
}

export function listAssetCategories() {
  return api<{ data: AssetCategory[] }>('/api/v1/assets/categories');
}

export function createAssetCategory(body: {
  categoryCode: string;
  categoryName: string;
  groupType: 'it_assets' | 'communication' | 'office' | 'travel_ops' | 'software_digital' | 'other';
  prefix: string;
  usefulLifeMonths?: number;
  defaultDepreciationRate?: number;
  description?: string | null;
}) {
  return api<{ data: AssetCategory }>('/api/v1/assets/categories', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listAssets(params: {
  search?: string;
  category?: string;
  categoryId?: string;
  status?: string;
  departmentId?: string;
  location?: string;
  employeeId?: string;
  projectId?: string;
  vendorId?: string;
  purchaseYear?: string;
  warrantyExpiringSoon?: boolean;
} = {}) {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.category) q.set('category', params.category);
  if (params.categoryId) q.set('categoryId', params.categoryId);
  if (params.status) q.set('status', params.status);
  if (params.departmentId) q.set('departmentId', params.departmentId);
  if (params.location) q.set('location', params.location);
  if (params.employeeId) q.set('employeeId', params.employeeId);
  if (params.projectId) q.set('projectId', params.projectId);
  if (params.vendorId) q.set('vendorId', params.vendorId);
  if (params.purchaseYear) q.set('purchaseYear', params.purchaseYear);
  if (params.warrantyExpiringSoon) q.set('warrantyExpiringSoon', 'true');
  const qs = q.toString() ? `?${q.toString()}` : '';
  return api<{ data: Asset[] }>(`/api/v1/assets${qs}`);
}

export function getAssetDetail(id: string) {
  return api<{ data: AssetDetail }>(`/api/v1/assets/${id}`);
}

export function listMyAssets() {
  return api<{ data: EmployeeAsset[] }>('/api/v1/assets/my');
}

export function createAsset(body: {
  assetCode?: string;
  categoryId?: string | null;
  category: string;
  assetName?: string | null;
  brand?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  imei?: string | null;
  macAddress?: string | null;
  purchaseDate?: string | null;
  purchaseCost?: number | null;
  vendorId?: string | null;
  warrantyStart?: string | null;
  warrantyEnd?: string | null;
  warrantyProvider?: string | null;
  warrantyNotes?: string | null;
  location?: string | null;
  departmentId?: string | null;
  costCenter?: string | null;
  projectId?: string | null;
  condition?: string;
  status?: string;
  usefulLifeMonths?: number | null;
  depreciationMethod?: string | null;
  salvageValue?: number | null;
  notes?: string | null;
  specifications?: Record<string, any> | null;
  accessories?: Array<{
    name: string;
    accessoryType?: 'charger' | 'mouse' | 'bag' | 'cable' | 'dongle' | 'case' | 'other';
    serialNumber?: string | null;
    condition?: string;
  }>;
}) {
  return api<{ data: Asset }>('/api/v1/assets', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateAsset(id: string, body: Partial<Parameters<typeof createAsset>[0]>) {
  return api<{ data: Asset }>(`/api/v1/assets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function disposeAsset(id: string, body: { disposalDate: string; reason: string; disposalProceeds?: number; notes?: string }) {
  return api<{ data: Asset }>(`/api/v1/assets/${id}/dispose`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function allocateAsset(body: {
  assetId: string;
  employeeId: string;
  expectedReturnDate?: string | null;
  allocationType?: 'permanent' | 'temporary' | 'project_based';
  issuedCondition?: string;
  accessoriesIncluded?: string[];
  notes?: string | null;
}) {
  return api<{ data: any }>('/api/v1/assets/allocate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function acknowledgeAsset(allocationId: string, body: { termsVersion?: string; comments?: string | null; status?: 'accepted' | 'disputed' } = {}) {
  return api<{ data: any }>(`/api/v1/assets/allocations/${allocationId}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function transferAsset(body: {
  assetId: string;
  transferType: 'employee_to_employee' | 'department_to_department' | 'location_to_location';
  toEmployeeId?: string | null;
  toDepartmentId?: string | null;
  toLocation?: string | null;
  conditionAtTransfer?: string;
  reason: string;
}) {
  return api<{ data: any }>('/api/v1/assets/transfer', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function returnAsset(allocationId: string, condition: string) {
  return api<{ data: any }>(`/api/v1/assets/allocations/${allocationId}/return`, {
    method: 'POST',
    body: JSON.stringify({ conditionOnReturn: condition }),
  });
}

export function inspectAndReturnAsset(allocationId: string, body: {
  conditionOnReturn: string;
  accessoriesReturned?: string[];
  missingAccessories?: string[];
  hasDamage?: boolean;
  damageDescription?: string | null;
  inspectionResult?: 'passed' | 'minor_wear' | 'repair_required' | 'total_loss';
  inspectorRemarks?: string | null;
  photosStorageKeys?: string[];
}) {
  return api<{ data: any }>(`/api/v1/assets/allocations/${allocationId}/return`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createAssetIncident(body: {
  assetId: string;
  employeeId?: string | null;
  incidentType: 'damaged' | 'lost' | 'stolen' | 'missing_accessory';
  incidentDate: string;
  description: string;
  estimatedCost: number;
  employeeResponsibilityPct?: number;
  proposedRecoveryAmount?: number;
}) {
  return api<{ data: any }>('/api/v1/assets/incidents', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function reviewAssetIncident(id: string, body: {
  status: 'under_investigation' | 'approved_for_recovery' | 'waived' | 'closed';
  reviewRemarks?: string | null;
  approvedRecoveryAmount?: number;
  recoveryMode?: 'payroll_deduction' | 'fnf_settlement' | 'direct_payment' | 'waived';
  payrollMonth?: string | null;
}) {
  return api<{ data: any }>(`/api/v1/assets/incidents/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createAssetMaintenance(body: {
  assetId: string;
  vendorId?: string | null;
  serviceType: 'preventive' | 'repair' | 'upgrade' | 'warranty_claim';
  issueDescription: string;
  startDate: string;
  repairCost?: number;
  isWarrantyCovered?: boolean;
  invoiceRef?: string | null;
  nextScheduledMaintenance?: string | null;
}) {
  return api<{ data: any }>('/api/v1/assets/maintenance', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function completeAssetMaintenance(id: string, body: {
  completionDate: string;
  downtimeDays?: number;
  repairCost?: number;
  resolutionNotes?: string;
  nextScheduledMaintenance?: string | null;
}) {
  return api<{ data: any }>(`/api/v1/assets/maintenance/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getAssetReport(reportType: string, params: Record<string, any> = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  const qs = q.toString() ? `?${q.toString()}` : '';
  return api<{ data: { reportType: string; rows: any[] } }>(`/api/v1/assets/reports/${reportType}${qs}`);
}

// ----------------- RECRUITMENT (ATS) -----------------
export interface JobOpening {
  id: string;
  organization_id: string;
  department_id: string | null;
  title: string;
  code: string;
  vacancies: number;
  job_type: string;
  experience_level: string | null;
  location: string | null;
  description: string | null;
  status: string;
  created_at: string;
  department_name: string | null;
}

export interface JobApplication {
  id: string;
  job_opening_id: string;
  candidate_id: string;
  stage: 'applied' | 'screening' | 'interview' | 'offer' | 'hired' | 'rejected';
  rating: number | null;
  feedback: string | null;
  applied_at: string;
  job_title: string;
  job_code: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  current_company: string | null;
  expected_ctc: number | null;
  notice_period_days: number | null;
}

export function listJobOpenings() {
  return api<{ data: JobOpening[] }>('/api/v1/recruitment/job-openings');
}

export function createJobOpening(body: {
  organizationId: string;
  departmentId?: string | null;
  title: string;
  code: string;
  vacancies?: number;
  jobType?: string;
  experienceLevel?: string | null;
  location?: string | null;
  description?: string | null;
}) {
  return api<{ data: JobOpening }>('/api/v1/recruitment/job-openings', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function parseResume(text: string) {
  return api<{ data: any }>('/api/v1/recruitment/parse-resume', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export function listApplications(jobOpeningId?: string) {
  const q = jobOpeningId ? `?jobOpeningId=${jobOpeningId}` : '';
  return api<{ data: JobApplication[] }>(`/api/v1/recruitment/applications${q}`);
}

export function submitApplication(jobOpeningId: string, body: {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  currentCompany?: string | null;
  currentCtc?: number | null;
  expectedCtc?: number | null;
  noticePeriodDays?: number | null;
  headline?: string | null;
  summary?: string | null;
  totalExperienceYears?: number | null;
  currentLocation?: string | null;
  preferredLocations?: string | null;
  skills?: string | null;
  education?: Array<{ degree: string; institution: string; year: number; gpa?: string }>;
  experience?: Array<{ title: string; company: string; duration: string; summary?: string }>;
  certifications?: Array<{ name: string; issuer: string; year: number }>;
  portfolioLinks?: string | null;
  rawResumeText?: string | null;
}) {
  return api<{ data: any }>(`/api/v1/recruitment/job-openings/${jobOpeningId}/apply`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateApplicationStage(id: string, body: { stage: string; rating?: number | null; feedback?: string | null }) {
  return api<{ data: JobApplication }>(`/api/v1/recruitment/applications/${id}/stage`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function scheduleInterview(body: {
  applicationId: string;
  roundName: string;
  interviewerEmployeeId: string;
  scheduledAt: string;
  durationMinutes?: number;
  meetingLink?: string | null;
}) {
  return api<{ data: any }>('/api/v1/recruitment/interviews/schedule', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function submitInterviewEvaluation(body: {
  interviewId: string;
  technicalScore?: number | null;
  culturalScore?: number | null;
  communicationScore?: number | null;
  overallScore?: number | null;
  recommendation: 'strong_hire' | 'hire' | 'hold' | 'reject';
  strengths?: string | null;
  areasOfImprovement?: string | null;
  detailedFeedback?: string | null;
  criteriaRatings?: Record<string, number>;
  isConfidential?: boolean;
}) {
  return api<{ data: any }>('/api/v1/recruitment/interviews/evaluate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listOfferTemplates() {
  return api<{ data: Array<{ id: string; template_name: string; template_type: string; template_body: string; required_variables: string | null }> }>('/api/v1/recruitment/offers/templates');
}

export function generateOffer(body: {
  applicationId: string;
  templateId?: string | null;
  offeredDesignation: string;
  offeredCtc: number;
  basicMonthly?: number;
  hraMonthly?: number;
  otherAllowanceMonthly?: number;
  joiningDate: string;
  departmentId?: string | null;
  location?: string | null;
}) {
  return api<{ data: any }>('/api/v1/recruitment/offers/generate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function convertCandidateToEmployee(body: {
  offerId: string;
  employeeCode?: string;
  dateOfJoining?: string;
  departmentId: string;
  designation: string;
  managerId?: string | null;
  ctcAnnual: number;
  basicMonthly?: number;
  hraMonthly?: number;
  otherAllowanceMonthly?: number;
  employmentType?: string;
  location?: string;
  generateUserAccount?: boolean;
}) {
  return api<{ data: any }>('/api/v1/recruitment/offers/convert-to-employee', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ----------------- ONBOARDING -----------------
export interface OnboardingWorkflow {
  id: string;
  employee_id: string;
  template_name: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'on_hold';
  target_completion_date: string | null;
  completed_at: string | null;
  overall_progress_percent: number;
  probation_months: number;
  probation_end_date: string | null;
  confirmation_status: 'pending' | 'confirmed' | 'extended' | 'terminated';
  confirmed_by: string | null;
  confirmed_at: string | null;
  notes: string | null;
}

export interface OnboardingTask {
  id: string;
  workflow_id: string;
  stage_name: string;
  task_title: string;
  task_description: string | null;
  assigned_role: string;
  assigned_to_employee_id: string | null;
  is_mandatory: number;
  status: 'pending' | 'in_progress' | 'completed' | 'waived';
  completed_at: string | null;
  completed_by: string | null;
  supporting_doc_key: string | null;
  remarks: string | null;
}

export function getEmployeeOnboarding(employeeId: string) {
  return api<{ data: { workflow: OnboardingWorkflow | null; tasks: OnboardingTask[] } }>(`/api/v1/onboarding/employee/${employeeId}`);
}

export function updateOnboardingTask(taskId: string, body: {
  status: 'pending' | 'in_progress' | 'completed' | 'waived';
  remarks?: string | null;
  supportingDocKey?: string | null;
}) {
  return api<{ data: OnboardingTask }>(`/api/v1/onboarding/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function confirmProbation(workflowId: string, body: {
  confirmationStatus: 'confirmed' | 'extended' | 'terminated';
  notes?: string | null;
  extendMonths?: number;
}) {
  return api<{ data: OnboardingWorkflow }>(`/api/v1/onboarding/workflows/${workflowId}/confirm-probation`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ----------------- CAREER, PROMOTIONS & TRANSFERS -----------------
export interface CareerPromotion {
  id: string;
  employee_id: string;
  effective_date: string;
  previous_designation: string;
  new_designation: string;
  previous_ctc: number | null;
  new_ctc: number | null;
  previous_grade: string | null;
  new_grade: string | null;
  justification: string;
  status: 'submitted' | 'approved' | 'rejected' | 'applied';
  approval_remarks: string | null;
  created_at: string;
  first_name: string;
  last_name: string;
  employee_code: string;
  previous_department_name?: string | null;
  new_department_name?: string | null;
}

export interface EmployeeTransfer {
  id: string;
  employee_id: string;
  transfer_type: string;
  effective_date: string;
  from_location: string | null;
  to_location: string | null;
  reason: string;
  status: string;
  created_at: string;
  first_name: string;
  last_name: string;
  employee_code: string;
  from_department_name?: string | null;
  to_department_name?: string | null;
}

export interface SalaryRevision {
  id: string;
  employee_id: string;
  effective_from: string;
  revision_type: string;
  previous_ctc_annual: number;
  new_ctc_annual: number;
  increment_percentage: number | null;
  previous_basic_monthly: number | null;
  new_basic_monthly: number | null;
  revision_reason: string;
  created_at: string;
  first_name: string;
  last_name: string;
  employee_code: string;
}

export function listPromotions(employeeId?: string) {
  const q = employeeId ? `?employeeId=${employeeId}` : '';
  return api<{ data: CareerPromotion[] }>(`/api/v1/career/promotions${q}`);
}

export function requestPromotion(body: {
  employeeId: string;
  effectiveDate: string;
  newDesignation: string;
  newDepartmentId?: string | null;
  newCtc?: number | null;
  newGrade?: string | null;
  justification: string;
}) {
  return api<{ data: CareerPromotion }>('/api/v1/career/promotions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function reviewPromotion(id: string, body: { status: 'approved' | 'rejected'; remarks?: string | null }) {
  return api<{ data: CareerPromotion }>(`/api/v1/career/promotions/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listTransfers(employeeId?: string) {
  const q = employeeId ? `?employeeId=${employeeId}` : '';
  return api<{ data: EmployeeTransfer[] }>(`/api/v1/career/transfers${q}`);
}

export function executeTransfer(body: {
  employeeId: string;
  transferType: string;
  effectiveDate: string;
  toDepartmentId?: string | null;
  toLocation?: string | null;
  toManagerId?: string | null;
  reason: string;
}) {
  return api<{ data: EmployeeTransfer }>('/api/v1/career/transfers', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listSalaryRevisions(employeeId?: string) {
  const q = employeeId ? `?employeeId=${employeeId}` : '';
  return api<{ data: SalaryRevision[] }>(`/api/v1/career/salary-revisions${q}`);
}

export function createSalaryRevision(body: {
  employeeId: string;
  effectiveFrom: string;
  revisionType?: string;
  newCtcAnnual: number;
  newBasicMonthly?: number | null;
  newHraMonthly?: number | null;
  newOtherMonthly?: number | null;
  revisionReason: string;
}) {
  return api<{ data: SalaryRevision }>('/api/v1/career/salary-revisions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ----------------- GRIEVANCES & COMPLAINTS -----------------
export interface EmployeeGrievance {
  id: string;
  grievance_number: string;
  complainant_employee_id: string;
  is_anonymous: number;
  category: string;
  severity: string;
  subject: string;
  description: string;
  against_person_name: string | null;
  against_employee_id: string | null;
  status: string;
  investigator_employee_id: string | null;
  resolution_summary: string | null;
  closure_reason: string | null;
  retaliation_protection_enabled: number;
  acknowledged_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  created_at: string;
  complainant_first_name?: string;
  complainant_last_name?: string;
  complainant_code?: string;
  against_first_name?: string;
  against_last_name?: string;
  investigator_first_name?: string;
  investigator_last_name?: string;
}

export function listGrievances(params: { category?: string; status?: string; myOnly?: boolean } = {}) {
  const q = new URLSearchParams();
  if (params.category) q.set('category', params.category);
  if (params.status) q.set('status', params.status);
  if (params.myOnly) q.set('myOnly', 'true');
  return api<{ data: EmployeeGrievance[] }>(`/api/v1/grievances?${q.toString()}`);
}

export function getGrievance(id: string) {
  return api<{ data: { grievance: EmployeeGrievance; activities: any[] } }>(`/api/v1/grievances/${id}`);
}

export function submitGrievance(body: {
  isAnonymous?: boolean;
  category: string;
  severity?: string;
  subject: string;
  description: string;
  againstPersonName?: string | null;
  againstEmployeeId?: string | null;
  evidenceAttachments?: string | null;
}) {
  return api<{ data: EmployeeGrievance }>('/api/v1/grievances', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function assignGrievanceInvestigator(id: string, investigatorEmployeeId: string) {
  return api<{ data: EmployeeGrievance }>(`/api/v1/grievances/${id}/assign`, {
    method: 'POST',
    body: JSON.stringify({ investigatorEmployeeId }),
  });
}

export function addGrievanceActivity(id: string, body: {
  activityType: string;
  summary: string;
  confidentialNotes?: string | null;
  evidenceKey?: string | null;
  actionTaken?: string | null;
}) {
  return api<{ data: any }>(`/api/v1/grievances/${id}/activities`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function resolveGrievance(id: string, body: {
  status: 'resolved' | 'closed';
  resolutionSummary: string;
  closureReason?: string | null;
}) {
  return api<{ data: EmployeeGrievance }>(`/api/v1/grievances/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ----------------- GRATUITY MANAGEMENT -----------------
export interface GratuityRule {
  id: string;
  rule_code: string;
  rule_name: string;
  jurisdiction: string;
  min_service_years: number;
  formula_expression: string;
  max_statutory_ceiling: number;
  applicable_components: string;
  is_active: number;
  effective_from: string;
  disclaimer: string | null;
}

export interface GratuityRecord {
  id: string;
  employee_id: string;
  resignation_id: string | null;
  date_of_joining: string;
  date_of_exit: string;
  total_service_years: number;
  qualifying_service_years: number;
  last_drawn_basic: number;
  estimated_gratuity_amount: number;
  approved_gratuity_amount: number | null;
  payment_status: string;
  legal_determination_notes: string | null;
  created_at: string;
  first_name: string;
  last_name: string;
  employee_code: string;
  rule_name?: string;
  disclaimer?: string;
}

export function listGratuityRules() {
  return api<{ data: GratuityRule[] }>('/api/v1/gratuity/rules');
}

export function listGratuityRecords(employeeId?: string) {
  const q = employeeId ? `?employeeId=${employeeId}` : '';
  return api<{ data: GratuityRecord[] }>(`/api/v1/gratuity/records${q}`);
}

export function calculateGratuity(body: {
  employeeId: string;
  resignationId?: string | null;
  dateOfExit?: string | null;
  overrideLastBasic?: number | null;
}) {
  return api<{ data: GratuityRecord }>('/api/v1/gratuity/calculate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function approveGratuity(id: string, body: {
  approvedGratuityAmount: number;
  legalDeterminationNotes?: string | null;
  supportingDocKey?: string | null;
}) {
  return api<{ data: GratuityRecord }>(`/api/v1/gratuity/records/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ----------------- EXIT & CLEARANCE & FNF -----------------
export interface Resignation {
  id: string;
  employee_id: string;
  submission_date: string;
  requested_last_working_date: string;
  official_last_working_date: string | null;
  reason: string;
  status: 'submitted' | 'approved' | 'rejected' | 'completed';
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  first_name: string;
  last_name: string;
  employee_code: string;
  designation: string | null;
}

export interface ClearanceTask {
  id: string;
  resignation_id: string;
  department_name: string;
  task_name: string;
  status: 'pending' | 'cleared' | 'flagged';
  cleared_by: string | null;
  cleared_at: string | null;
  remarks: string | null;
  created_at: string;
}

export interface FnfSettlement {
  id: string;
  resignation_id: string;
  employee_id: string;
  gross_payable: number;
  leave_encashment_amount: number;
  gratuity_amount: number;
  bonus_incentive: number;
  total_earnings: number;
  loan_recovery_amount: number;
  notice_period_shortfall_deduction: number;
  asset_damage_deduction: number;
  tax_tds_deduction: number;
  total_deductions: number;
  net_settlement_amount: number;
  status: string;
  disbursed_at: string | null;
  created_at: string;
}

export interface ExitInterview {
  id: string;
  resignation_id: string;
  employee_id: string;
  primary_reason_for_leaving: string;
  job_satisfaction_rating: number | null;
  work_environment_rating: number | null;
  management_support_rating: number | null;
  compensation_satisfaction_rating: number | null;
  career_growth_rating: number | null;
  culture_rating: number | null;
  what_went_well: string | null;
  what_could_improve: string | null;
  recommend_organization: number;
  feedback_to_leadership: string | null;
}

export interface AlumniRecord {
  id: string;
  employee_id: string;
  employee_code: string;
  full_name: string;
  personal_email: string | null;
  personal_phone: string | null;
  linkedin_url: string | null;
  last_designation: string;
  last_department_name: string | null;
  date_of_joining: string;
  date_of_exit: string;
  exit_type: string;
  rehire_eligibility: string;
  rehire_notes: string | null;
  relieving_letter_key: string | null;
  experience_certificate_key: string | null;
}

export function submitExitInterview(resignationId: string, body: {
  primaryReasonForLeaving: string;
  jobSatisfactionRating?: number | null;
  workEnvironmentRating?: number | null;
  managementSupportRating?: number | null;
  compensationSatisfactionRating?: number | null;
  careerGrowthRating?: number | null;
  cultureRating?: number | null;
  whatWentWell?: string | null;
  whatCouldImprove?: string | null;
  recommendOrganization?: boolean;
  feedbackToLeadership?: string | null;
  confidentialHrNotes?: string | null;
}) {
  return api<{ data: ExitInterview }>(`/api/v1/exit/resignations/${resignationId}/exit-interview`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getExitInterview(resignationId: string) {
  return api<{ data: ExitInterview }>(`/api/v1/exit/resignations/${resignationId}/exit-interview`);
}

export function convertToAlumni(body: {
  employeeId: string;
  personalEmail?: string | null;
  personalPhone?: string | null;
  linkedinUrl?: string | null;
  rehireEligibility?: 'eligible' | 'ineligible' | 'conditional';
  rehireNotes?: string | null;
  relievingLetterKey?: string | null;
  experienceCertificateKey?: string | null;
  notes?: string | null;
}) {
  return api<{ data: AlumniRecord }>('/api/v1/exit/alumni/convert', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listAlumni(search?: string) {
  const q = search ? `?search=${encodeURIComponent(search)}` : '';
  return api<{ data: AlumniRecord[] }>(`/api/v1/exit/alumni${q}`);
}

export function listResignations(params: { employeeId?: string; viewAll?: boolean } = {}) {
  const q = new URLSearchParams();
  if (params.employeeId) q.set('employeeId', params.employeeId);
  if (params.viewAll) q.set('viewAll', 'true');
  return api<{ data: Resignation[] }>(`/api/v1/exit/resignations?${q.toString()}`);
}

export function submitResignation(body: { requestedLastWorkingDate: string; reason: string }) {
  return api<{ data: Resignation }>('/api/v1/exit/resignations', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function reviewResignation(id: string, body: { status: 'approved' | 'rejected'; officialLastDate: string }) {
  return api<{ data: Resignation }>(`/api/v1/exit/resignations/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listClearanceTasks(resignationId: string) {
  return api<{ data: ClearanceTask[] }>(`/api/v1/exit/resignations/${resignationId}/clearances`);
}

export function signoffClearanceTask(taskId: string, body: { status: 'cleared' | 'flagged'; remarks?: string | null }) {
  return api<{ data: ClearanceTask }>(`/api/v1/exit/clearance-tasks/${taskId}/signoff`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function finalizeFnFSettlement(resignationId: string) {
  return api<{ data: FnfSettlement }>(`/api/v1/exit/resignations/${resignationId}/settle`, {
    method: 'POST',
    body: '{}',
  });
}


// ----------------- PROJECTS & OPERATIONS -----------------
export interface Project {
  id: string;
  organization_id: string;
  code: string;
  name: string;
  client_name: string | null;
  budget: number | null;
  status: string;
  created_at: string;
}

export interface Timesheet {
  id: string;
  employee_id: string;
  project_id: string;
  work_date: string;
  hours: number;
  is_billable: number;
  task_description: string;
  status: string;
  created_at: string;
  project_name: string;
  project_code: string;
  first_name: string;
  last_name: string;
  employee_code: string;
}

export function listProjects() {
  return api<{ data: Project[] }>('/api/v1/operations/projects');
}

export function createProject(body: { organizationId: string; code: string; name: string; clientName?: string | null; budget?: number | null }) {
  return api<{ data: Project }>('/api/v1/operations/projects', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listTimesheets(params: { employeeId?: string; viewAll?: boolean } = {}) {
  const q = new URLSearchParams();
  if (params.employeeId) q.set('employeeId', params.employeeId);
  if (params.viewAll) q.set('viewAll', 'true');
  return api<{ data: Timesheet[] }>(`/api/v1/operations/timesheets?${q.toString()}`);
}

export function submitTimesheet(body: { projectId: string; workDate: string; hours: number; isBillable?: boolean; taskDescription: string }) {
  return api<{ data: Timesheet }>('/api/v1/operations/timesheets', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ----------------- FINANCE & CONTROL -----------------
export interface FinancialAccount {
  id: string;
  organization_id: string;
  account_code: string;
  account_name: string;
  type: 'bank' | 'cash' | 'petty_cash' | 'credit_card';
  bank_name: string | null;
  account_number: string | null;
  ifsc_code: string | null;
  branch_name: string | null;
  currency: string;
  opening_balance: number;
  current_balance: number;
  is_active: number;
  created_at: string;
}

export interface FinancialTransaction {
  id: string;
  transaction_number: string;
  transaction_type: 'income' | 'expense' | 'transfer' | 'payroll' | 'vendor_payment' | 'customer_receipt';
  transaction_date: string;
  amount: number;
  currency: string;
  account_id: string;
  to_account_id: string | null;
  department_id: string | null;
  project_id: string | null;
  employee_id: string | null;
  vendor_id: string | null;
  customer_id: string | null;
  category: string;
  description: string | null;
  reference_number: string | null;
  supporting_doc_key: string | null;
  status: 'draft' | 'posted' | 'reconciled' | 'void';
  created_by: string;
  created_at: string;
}

export interface Vendor {
  id: string;
  organization_id: string;
  vendor_code: string;
  legal_name: string;
  trade_name: string | null;
  category: string;
  gstin: string | null;
  pan: string | null;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_ifsc: string | null;
  payment_terms_days: number;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  status: 'active' | 'inactive' | 'blacklisted';
  created_at: string;
}

export interface Client {
  id: string;
  organization_id: string;
  client_code: string;
  name: string;
  trade_name: string | null;
  gstin: string | null;
  pan: string | null;
  billing_address: string | null;
  payment_terms_days: number;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  status: 'active' | 'inactive';
  created_at: string;
}

export interface Invoice {
  id: string;
  organization_id: string;
  invoice_number: string;
  invoice_type: 'customer_invoice' | 'vendor_bill';
  client_id: string | null;
  vendor_id: string | null;
  project_id: string | null;
  issue_date: string;
  due_date: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  paid_amount: number;
  currency: string;
  notes: string | null;
  status: 'draft' | 'issued' | 'partially_paid' | 'paid' | 'overdue' | 'cancelled';
  created_at: string;
}

export interface Budget {
  id: string;
  fiscal_year: string;
  department_id: string | null;
  project_id: string | null;
  category: string;
  allocated_amount: number;
  committed_amount: number;
  utilized_amount: number;
  alert_threshold_percent: number;
  utilizationPercent: number;
  remainingAmount: number;
  alertStatus: 'normal' | 'warning' | 'critical' | 'exceeded';
  created_at: string;
}

export interface FinanceOverview {
  balances: {
    totalBank: number;
    totalCash: number;
    totalLiquidFunds: number;
  };
  payables: {
    totalPayables: number;
    payablesOverdue: number;
    openBillsCount: number;
  };
  receivables: {
    totalReceivables: number;
    receivablesOverdue: number;
    openInvoicesCount: number;
  };
  mtd: {
    month: string;
    income: number;
    expenses: number;
    netCashFlow: number;
  };
  accounts: FinancialAccount[];
  recentTransactions: FinancialTransaction[];
}

export interface ManagementPL {
  period: { fromDate: string; toDate: string };
  revenue: number;
  directCosts: number;
  grossProfit: number;
  grossMarginPercent: number;
  operatingCosts: number;
  netOperatingProfit: number;
  netMarginPercent: number;
  breakdown: {
    revenueByCat: Record<string, number>;
    directCostsByCat: Record<string, number>;
    operatingCostsByCat: Record<string, number>;
  };
}

export function getFinanceOverview() {
  return api<{ data: FinanceOverview }>('/api/v1/finance/overview');
}

export function listFinancialAccounts() {
  return api<{ data: FinancialAccount[] }>('/api/v1/finance/accounts');
}

export function createFinancialAccount(body: {
  organizationId: string;
  accountCode: string;
  accountName: string;
  type: 'bank' | 'cash' | 'petty_cash' | 'credit_card';
  bankName?: string | null;
  accountNumber?: string | null;
  ifscCode?: string | null;
  branchName?: string | null;
  currency?: string;
  openingBalance?: number;
}) {
  return api<{ data: FinancialAccount }>('/api/v1/finance/accounts', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listFinancialTransactions(params: {
  transactionType?: string;
  accountId?: string;
  departmentId?: string;
  projectId?: string;
  category?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const q = new URLSearchParams();
  if (params.transactionType) q.set('transactionType', params.transactionType);
  if (params.accountId) q.set('accountId', params.accountId);
  if (params.departmentId) q.set('departmentId', params.departmentId);
  if (params.projectId) q.set('projectId', params.projectId);
  if (params.category) q.set('category', params.category);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  return api<{ data: FinancialTransaction[] }>(`/api/v1/finance/transactions?${q.toString()}`);
}

export function recordFinancialTransaction(body: {
  organizationId: string;
  transactionType: 'income' | 'expense' | 'transfer' | 'payroll' | 'vendor_payment' | 'customer_receipt';
  transactionDate: string;
  amount: number;
  currency?: string;
  accountId: string;
  toAccountId?: string | null;
  departmentId?: string | null;
  projectId?: string | null;
  employeeId?: string | null;
  vendorId?: string | null;
  customerId?: string | null;
  category: string;
  description?: string | null;
  referenceNumber?: string | null;
  supportingDocKey?: string | null;
}) {
  return api<{ data: FinancialTransaction }>('/api/v1/finance/transactions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listVendors() {
  return api<{ data: Vendor[] }>('/api/v1/finance/vendors');
}

export function createVendor(body: {
  organizationId: string;
  vendorCode: string;
  legalName: string;
  tradeName?: string | null;
  category: string;
  gstin?: string | null;
  pan?: string | null;
  bankName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  paymentTermsDays?: number;
  contactPerson?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}) {
  return api<{ data: Vendor }>('/api/v1/finance/vendors', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listClients() {
  return api<{ data: Client[] }>('/api/v1/finance/clients');
}

export function createClient(body: {
  organizationId: string;
  clientCode: string;
  name: string;
  tradeName?: string | null;
  gstin?: string | null;
  pan?: string | null;
  billingAddress?: string | null;
  paymentTermsDays?: number;
  contactPerson?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}) {
  return api<{ data: Client }>('/api/v1/finance/clients', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listInvoices(params: { invoiceType?: 'customer_invoice' | 'vendor_bill'; status?: string } = {}) {
  const q = new URLSearchParams();
  if (params.invoiceType) q.set('invoiceType', params.invoiceType);
  if (params.status) q.set('status', params.status);
  return api<{ data: Invoice[] }>(`/api/v1/finance/invoices?${q.toString()}`);
}

export function createInvoice(body: {
  organizationId: string;
  invoiceNumber: string;
  invoiceType: 'customer_invoice' | 'vendor_bill';
  clientId?: string | null;
  vendorId?: string | null;
  projectId?: string | null;
  issueDate: string;
  dueDate: string;
  subtotal: number;
  taxAmount?: number;
  currency?: string;
  notes?: string | null;
}) {
  return api<{ data: Invoice }>('/api/v1/finance/invoices', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function settleInvoice(id: string, body: { amount: number; accountId: string; paymentReference?: string | null }) {
  return api<{ data: { id: string; paidAmount: number; status: string } }>(`/api/v1/finance/invoices/${id}/settle`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listBudgets(fiscalYear?: string) {
  const q = fiscalYear ? `?fiscalYear=${fiscalYear}` : '';
  return api<{ data: Budget[] }>(`/api/v1/finance/budgets${q}`);
}

export function setBudget(body: {
  fiscalYear: string;
  departmentId?: string | null;
  projectId?: string | null;
  category?: string;
  allocatedAmount: number;
  alertThresholdPercent?: number;
}) {
  return api<{ data: Budget }>('/api/v1/finance/budgets', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getManagementPL(params: { fromDate?: string; toDate?: string } = {}) {
  const q = new URLSearchParams();
  if (params.fromDate) q.set('fromDate', params.fromDate);
  if (params.toDate) q.set('toDate', params.toDate);
  return api<{ data: ManagementPL }>(`/api/v1/finance/pl?${q.toString()}`);
}

// ----------------- ADMIN BULK INGESTION STUDIO -----------------
export interface BulkImportResult {
  entity: string;
  totalRecords: number;
  insertedCount: number;
  skippedCount: number;
  errors: Array<{ row: number; identifier?: string; message: string }>;
}

export function bulkImportData(entity: 'employees' | 'attendance' | 'invoices' | 'assets' | 'candidates', rows: Record<string, any>[]) {
  return api<{ status: string; data: BulkImportResult }>('/api/v1/admin/bulk-import', {
    method: 'POST',
    body: JSON.stringify({ entity, rows }),
  });
}

// ----------------- UNIVERSAL HR DOCUMENT ENGINE -----------------
export interface DocumentTemplate {
  id: string;
  tenant_id: string;
  organization_id: string;
  name: string;
  category: 'recruitment' | 'joining' | 'employment' | 'performance' | 'payroll' | 'employee' | 'exit' | 'gratuity' | 'other';
  document_type: string;
  description: string | null;
  version: string;
  effective_date: string;
  language: string;
  legal_entity: string | null;
  employee_type_filter: string | null;
  department_id: string | null;
  template_body: string;
  header_html: string | null;
  footer_html: string | null;
  logo_storage_key: string | null;
  signature_blocks_json: string | null;
  required_variables_json: string | null;
  numbering_prefix: string;
  requires_approval: number;
  approval_workflow_steps_json: string | null;
  is_active: number;
  is_system: number;
  created_at: string;
  updated_at: string;
}

export interface GeneratedDocument {
  id: string;
  tenant_id: string;
  organization_id: string;
  template_id: string | null;
  template_version: string;
  document_number: string;
  category: string;
  document_type: string;
  title: string;
  employee_id: string | null;
  candidate_id: string | null;
  source_entity_type: string | null;
  source_entity_id: string | null;
  snapshot_data_json: string;
  rendered_content_html: string;
  pdf_storage_key: string | null;
  status: 'draft' | 'pending_review' | 'pending_approval' | 'approved' | 'rejected' | 'generated' | 'issued' | 'superseded' | 'cancelled' | 'archived';
  rejection_reason: string | null;
  issued_at: string | null;
  issued_by: string | null;
  expires_at: string | null;
  is_confidential: number;
  digital_signatures_json: string | null;
  created_at: string;
  updated_at: string;
  employee_first_name?: string;
  employee_last_name?: string;
  employee_code?: string;
  employee_designation?: string;
}

export interface VariableDefinition {
  key: string;
  label: string;
  category: string;
  description: string;
  example: string;
}

export interface BulkDocumentBatch {
  id: string;
  tenant_id: string;
  template_id: string;
  batch_name: string;
  total_count: number;
  success_count: number;
  failed_count: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'completed_with_errors';
  error_summary: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface DocumentAnalytics {
  totalGenerated: number;
  pendingApproval: number;
  issued: number;
  rejected: number;
  totalTemplates: number;
  activeTemplates: number;
  categoryBreakdown: Record<string, number>;
}

export function listDocumentTemplates(filter?: { category?: string; documentType?: string; isActive?: boolean }) {
  const q = new URLSearchParams();
  if (filter?.category) q.set('category', filter.category);
  if (filter?.documentType) q.set('documentType', filter.documentType);
  if (filter?.isActive !== undefined) q.set('isActive', String(filter.isActive));
  const queryStr = q.toString() ? `?${q.toString()}` : '';
  return api<{ data: DocumentTemplate[] }>(`/api/v1/documents/templates${queryStr}`);
}

export function getDocumentTemplate(id: string) {
  return api<{ data: DocumentTemplate }>(`/api/v1/documents/templates/${id}`);
}

export function createDocumentTemplate(body: {
  name: string;
  category: string;
  documentType: string;
  description?: string;
  version?: string;
  effectiveDate?: string;
  templateBody: string;
  headerHtml?: string;
  footerHtml?: string;
  signatureBlocks?: Array<{ role: string; name: string; designation?: string; order?: number }>;
  numberingPrefix?: string;
  requiresApproval?: boolean;
}) {
  return api<{ data: DocumentTemplate }>('/api/v1/documents/templates', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateDocumentTemplate(id: string, body: Partial<DocumentTemplate> & { incrementVersion?: boolean; isActive?: boolean }) {
  return api<{ data: DocumentTemplate }>(`/api/v1/documents/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function cloneDocumentTemplate(id: string, name?: string) {
  return api<{ data: DocumentTemplate }>(`/api/v1/documents/templates/${id}/clone`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export function getVariableCatalogue() {
  return api<{ data: VariableDefinition[] }>('/api/v1/documents/variables');
}

export function previewDocument(body: {
  templateId?: string;
  templateBody?: string;
  employeeId?: string | null;
  candidateId?: string | null;
  customVariables?: Record<string, any>;
}) {
  return api<{ data: { renderedHtml: string; rawText: string; resolvedVariables: Record<string, any> } }>('/api/v1/documents/preview', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function generateDocument(body: {
  templateId: string;
  employeeId?: string | null;
  candidateId?: string | null;
  title?: string | null;
  sourceEntityType?: string | null;
  sourceEntityId?: string | null;
  customVariables?: Record<string, any>;
  requiresApproval?: boolean;
  isConfidential?: boolean;
  expiresAt?: string | null;
}) {
  return api<{ data: GeneratedDocument }>('/api/v1/documents/generate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function reviewGeneratedDocument(id: string, body: { status: 'approved' | 'rejected'; remarks?: string }) {
  return api<{ data: { id: string; status: string } }>(`/api/v1/documents/generated/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function signGeneratedDocument(id: string, body: { signerRole: string; signerName?: string }) {
  return api<{ data: { id: string; signatures: any[] } }>(`/api/v1/documents/generated/${id}/sign`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function issueGeneratedDocument(id: string) {
  return api<{ data: { id: string; status: string; issuedAt: string } }>(`/api/v1/documents/generated/${id}/issue`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function bulkGenerateDocuments(body: {
  templateId: string;
  employeeIds: string[];
  batchName?: string;
  customVariables?: Record<string, any>;
}) {
  return api<{ data: { batchId: string; total: number; successes: number; failures: number; status: string } }>('/api/v1/documents/bulk-generate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getBulkDocumentBatch(id: string) {
  return api<{ data: BulkDocumentBatch }>(`/api/v1/documents/bulk-batches/${id}`);
}

export function searchDocumentVault(params: {
  employeeId?: string;
  category?: string;
  documentType?: string;
  status?: string;
  search?: string;
} = {}) {
  const q = new URLSearchParams();
  if (params.employeeId) q.set('employeeId', params.employeeId);
  if (params.category) q.set('category', params.category);
  if (params.documentType) q.set('documentType', params.documentType);
  if (params.status) q.set('status', params.status);
  if (params.search) q.set('search', params.search);
  const queryStr = q.toString() ? `?${q.toString()}` : '';
  return api<{ data: GeneratedDocument[] }>(`/api/v1/documents/vault${queryStr}`);
}

export function getVaultDocument(id: string) {
  return api<{ data: GeneratedDocument }>(`/api/v1/documents/vault/${id}`);
}

export function getDocumentAnalytics() {
  return api<{ data: DocumentAnalytics }>('/api/v1/documents/analytics');
}

export function getDocumentDownloadUrl(id: string) {
  return api<{ documentId: string; fileName: string; mimeType: string; sizeBytes: number; downloadRef: string; downloadUrl: string }>(`/api/v1/documents/${id}/download`);
}

export interface IamUser {
  id: string;
  email: string;
  fullName: string;
  status: 'active' | 'suspended';
  employeeId: string | null;
  employee: {
    employeeCode: string;
    name: string;
    designation: string | null;
    departmentId: string | null;
  } | null;
  roles: Array<{ key: string; name: string }>;
  roleKeys: string[];
  lastLoginAt: string | null;
  createdAt: string;
}

export interface IamRole {
  id: string;
  key: string;
  name: string;
  isSystem: boolean;
  permissions: string[];
}

export function listIamUsers() {
  return api<IamUser[]>('/api/v1/iam/users');
}

export function listIamRoles() {
  return api<IamRole[]>('/api/v1/iam/roles');
}

export function createIamUser(body: {
  email: string;
  fullName: string;
  password?: string;
  employeeId?: string | null;
  roles: string[];
}) {
  return api<{ id: string; email: string; fullName: string; status: string; roles: string[] }>('/api/v1/iam/users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateIamUserRoles(userId: string, roles: string[], reason?: string) {
  return api<{ userId: string; roles: Array<{ key: string; name: string }>; roleKeys: string[] }>(`/api/v1/iam/users/${userId}/roles`, {
    method: 'PUT',
    body: JSON.stringify({ roles, reason }),
  });
}

export function updateIamUserStatus(userId: string, status: 'active' | 'suspended') {
  return api<{ userId: string; status: string }>(`/api/v1/iam/users/${userId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function grantEmployeeAccess(employeeId: string, roles: string[]) {
  return api<any>(`/api/v1/employees/${employeeId}/access`, {
    method: 'POST',
    body: JSON.stringify({ roles }),
  });
}




// ----------------- ORGANIZATIONAL GOVERNANCE (migration 009) -----------------
// Thin wrappers over /api/v1/org. The server remains the authorization layer;
// nothing here decides what a user may do, it only asks.

export const PENDING = 'Pending Confirmation';

export interface OrgChartNode {
  employeeId: string;
  employeeCode: string;
  name: string;
  designation: string;
  employmentType: string;
  reportsTo: string | null;
  directReports: OrgChartNode[];
}

export interface OrgChart {
  activeHierarchy: OrgChartNode[];
  formerEmployees: Array<{ employeeId: string; name: string; designation: string; status: string; note: string }>;
  orphans: Array<{ employeeId: string; name: string; reason: string }>;
}

export function getOrgChart() {
  return api<OrgChart>('/api/v1/org/chart');
}

export interface ConfirmationQueue {
  totalOpenItems: number;
  byField: Record<string, number>;
  owners?: Record<string, string>;
  priority?: Record<string, 'critical' | 'high' | 'medium'>;
  legalIdentity?: {
    status: string;
    missingFields: string[];
    owner: string;
    priority: 'critical';
    blocks: string;
  };
  employees: Array<{
    employeeId: string; employeeCode: string; name: string;
    designation: string; openItems: number; gaps: string[];
  }>;
  note: string;
}

export function getConfirmationQueue() {
  return api<ConfirmationQueue>('/api/v1/org/confirmation-queue');
}

export interface OrgDashboard {
  people: {
    total: number; active: number; interns: number; formerEmployees: number;
    office: number; remote: number; hybrid: number;
    workArrangementPending: number; wfhRequestsOpen: number;
  };
  organization: {
    withoutManager: number; withoutDesignation: number;
    withoutDepartment: number; withoutJobDescription: number;
  };
  approvals: { pending: number; approved: number; rejected: number; escalated: number; unrouted: number };
  authority: { activeDelegations: number; expiringWithin30Days: number };
}

export function getOrgDashboard() {
  return api<OrgDashboard>('/api/v1/org/dashboard');
}

export interface JobDescription {
  id: string;
  version: string;
  status: 'draft' | 'active' | 'superseded';
  effectiveDate: string;
  supersedesId: string | null;
  changeReason: string;
  designation: string;
  administrativeManagerId: string | null;
  functionalManagerId: string | null;
  positionPurpose: string;
  responsibilities: string[] | string;
  kpis: string[] | string;
  authority: string[] | string;
  restrictions: string[] | string;
  approvedAt: string | null;
  acknowledged: boolean;
  acknowledgedAt: string | null;
}

export function listJobDescriptions(employeeId: string) {
  return api<{ items: JobDescription[] }>(`/api/v1/org/employees/${employeeId}/job-descriptions`);
}

export interface JobDescriptionDraft {
  employeeId: string;
  effectiveDate: string;
  changeReason: string;
  designation?: string | null;
  positionPurpose?: string | null;
  responsibilities?: string[];
  dailyDuties?: string[];
  weeklyDuties?: string[];
  monthlyDuties?: string[];
  kpis?: string[];
  authority?: string[];
  restrictions?: string[];
  skills?: string[];
  qualifications?: string[];
  confidentiality?: string | null;
  informationSecurity?: string | null;
  communicationRequirements?: string | null;
  reviewFrequency?: string | null;
}

export function createJobDescription(draft: JobDescriptionDraft) {
  return api<{ id: string; version: string; status: string; supersedes: string | null }>(
    '/api/v1/org/job-descriptions', { method: 'POST', body: JSON.stringify(draft) });
}

export function approveJobDescription(id: string) {
  return api<{ id: string; status: string }>(`/api/v1/org/job-descriptions/${id}/approve`, { method: 'POST', body: '{}' });
}

export function acknowledgeJobDescription(id: string) {
  return api<{ id: string; acknowledgedAt: string }>(`/api/v1/org/job-descriptions/${id}/acknowledge`, { method: 'POST', body: '{}' });
}

export interface WorkArrangement {
  employeeId: string;
  currentArrangement: string;
  requestedArrangement?: string | null;
  requestStatus: string;
  effectiveFrom?: string | null;
  reviewDate?: string | null;
  workingHours?: string;
  attendanceMethod?: string;
  equipmentIssued?: string;
  approvedAt?: string | null;
}

export function getWorkArrangement(employeeId: string) {
  return api<WorkArrangement>(`/api/v1/org/employees/${employeeId}/work-arrangement`);
}

export function requestWorkArrangement(input: {
  employeeId: string;
  requestedArrangement: 'office' | 'remote' | 'hybrid';
  reasonCategory?: string | null;
  proposedEffectiveFrom?: string | null;
  workingHours?: string | null;
  attendanceMethod?: string | null;
  equipmentIssued?: string | null;
  reviewDate?: string | null;
}) {
  return api<{ approvalRequestId: string; reference: string; status: string }>(
    '/api/v1/org/work-arrangement-requests', { method: 'POST', body: JSON.stringify(input) });
}

export interface ApprovalRow {
  id: string;
  reference: string;
  category: string;
  requestType: string;
  title: string;
  amount: number | null;
  currency: string;
  requestedBy: string | null;
  about: string | null;
  awaiting: string | null;
  actionableByYou: boolean;
  status: string;
  createdAt: string;
  decidedAt: string | null;
}

export function listApprovals() {
  return api<{ items: ApprovalRow[] }>('/api/v1/org/approvals');
}

export function raiseApproval(input: {
  category: 'hr' | 'finance' | 'operations' | 'technology' | 'commercial';
  requestType: string;
  title: string;
  subjectEmployeeId?: string | null;
  amount?: number | null;
  details?: Record<string, unknown>;
}) {
  return api<{ id: string; reference: string; steps: number; currentApproverEmployeeId: string | null }>(
    '/api/v1/org/approvals', { method: 'POST', body: JSON.stringify(input) });
}

export function decideApproval(id: string, decision: 'approved' | 'rejected', comments?: string) {
  return api<{ id: string; status: string; step: number; totalSteps: number }>(
    `/api/v1/org/approvals/${id}/decision`, { method: 'POST', body: JSON.stringify({ decision, comments }) });
}

export interface DelegationRow {
  id: string;
  delegator_employee_id: string;
  delegate_employee_id: string;
  authority_type: string;
  scope: string | null;
  monetary_limit: number | null;
  currency: string;
  starts_on: string;
  ends_on: string;
  reason: string;
  status: string;
}

export function listDelegations() {
  return api<{ items: DelegationRow[] }>('/api/v1/org/delegations');
}

export function createDelegation(input: {
  delegateEmployeeId: string; authorityType: string; scope?: string | null;
  monetaryLimit?: number | null; startsOn: string; endsOn: string; reason: string;
}) {
  return api<{ id: string; status: string }>('/api/v1/org/delegations', { method: 'POST', body: JSON.stringify(input) });
}

export function approveDelegation(id: string) {
  return api<{ id: string; status: string }>(`/api/v1/org/delegations/${id}/approve`, { method: 'POST', body: '{}' });
}

export function revokeDelegation(id: string, reason: string) {
  return api<{ id: string; status: string }>(`/api/v1/org/delegations/${id}/revoke`, { method: 'POST', body: JSON.stringify({ reason }) });
}

export interface MailboxRow {
  id: string; address: string; classification: string;
  purpose: string; ownerOfRecord: string; notes: string | null;
}

export function listMailboxes() {
  return api<{ items: MailboxRow[] }>('/api/v1/org/mailboxes');
}

export interface ComplianceRegister {
  items: Array<{
    id: string; item: string; category: string | null; applicability: string; status: string;
    responsibleEmployeeId: string; dueDate: string | null; reviewDate: string | null;
    professionalReviewRequired: boolean; professionalReviewedBy: string; notes: string | null;
  }>;
  poshAssessments: Array<{
    id: string; assessmentDate: string; workforceCounts: Record<string, number>;
    totalCounted: number; determination: string; confirmedByProfessional: string;
    icConstituted: boolean; annualReportDue: string | null;
  }>;
  note: string;
}

export function getComplianceRegister() {
  return api<ComplianceRegister>('/api/v1/org/compliance');
}

export function recordPoshAssessment(input: {
  workforceCounts: Record<string, number>;
  countingBasisNotes?: string | null;
  annualReportDue?: string | null;
}) {
  return api<{ id: string; totalCounted: number; determination: string }>(
    '/api/v1/org/compliance/posh-assessment', { method: 'POST', body: JSON.stringify(input) });
}

// ----------------- PHASE 3: identity, classification, directory -----------------

export interface OrganizationIdentity {
  exists: boolean;
  internalName: string;
  status: 'pending_management_confirmation' | 'confirmed';
  confirmedAt: string | null;
  missingFields: string[];
  identity: Record<string, string | null>;
  storedOrganizationRecord: {
    id: string; legalName: string; tradeName: string | null;
    country: string; pan: string | null; gstin: string | null; dataClassification: string;
  } | null;
  note: string;
}

export function getOrganizationIdentity() {
  return api<OrganizationIdentity>('/api/v1/org/identity');
}

export function updateOrganizationIdentity(input: Record<string, unknown>) {
  return api<OrganizationIdentity>('/api/v1/org/identity', { method: 'PUT', body: JSON.stringify(input) });
}

export interface DirectoryEmployee {
  employeeId: string;
  employeeCode: string;
  name: string;
  designation: string | null;
  departmentId: string | null;
  department: string | null;
  managerId: string | null;
  managerName: string | null;
  employmentType: string;
  status: string;
  workArrangement: string | null;
  workArrangementRequest: string | null;
  requestedArrangement: string | null;
  jdStatus: 'active' | 'draft' | 'none';
  jdVersion: string | null;
  kpiStatus: 'defined' | 'pending';
  joiningDateConfirmed: boolean;
  dataClassification: string;
  openItems: number;
  gaps: string[];
}

export function getDirectory() {
  return api<{ departments: Array<{ id: string; code: string; name: string }>; employees: DirectoryEmployee[] }>(
    '/api/v1/org/directory');
}

export interface EmployeeGovernance {
  organization: {
    department: string | null;
    designation: string | null;
    isTopAuthority: boolean;
    reportsTo: string | null;
    reportsToId: string | null;
    directReports: Array<{ id: string; name: string; designation: string | null }>;
    functionalAssignments: Array<{ area: string; functionalManager: string | null; isPrimary: boolean }>;
    businessUnit: string | null;
    organizationPath: string[];
  };
  jobDescription: {
    id: string; version: string; status: string; effectiveDate: string;
    responsibilities: string[] | null; kpis: string[] | null;
    acknowledged: boolean; acknowledgedAt: string | null; approvedAt: string | null; versions: number;
  } | null;
  workArrangement: {
    current: string | null; requested: string | null; requestStatus: string;
    effectiveFrom: string | null; reviewDate: string | null; approvedAt: string | null;
    history: Array<{ from: string | null; to: string | null; on: string; reason: string | null }>;
  } | null;
  authority: {
    delegatedToThem: Array<{ authorityType: string; scope: string | null; monetaryLimit: number | null; currency: string; startsOn: string; endsOn: string; status: string }>;
    delegatedByThem: Array<{ authorityType: string; delegate: string | null; monetaryLimit: number | null; status: string }>;
    restrictions: string[] | null;
    approvalAuthority: string[] | null;
  } | null;
  employmentHistory: Array<{ type: string; from: string | null; to: string | null; on: string; reason: string | null }>;
  approvals: { submitted: number; pending: number; approved: number; rejected: number; awaitingThem: number };
  governance: { joiningDateConfirmed: boolean; dataClassification: string };
}

export function getEmployeeGovernance(employeeId: string) {
  return api<EmployeeGovernance>(`/api/v1/org/employees/${employeeId}/governance`);
}

export function classifyEmployees(employeeIds: string[], classification: 'real' | 'demo' | 'unclassified') {
  return api<{ changed: number; classification: string }>('/api/v1/org/data-classification', {
    method: 'POST', body: JSON.stringify({ employeeIds, classification }),
  });
}
